import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    TextField,
    Typography,
} from '@mui/material';
// LinearProgress: pasek ładowania w nagłówku
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { SignInfo, signingService, toApiError } from './services/dictionaryService';
import SmsVerificationDialog from './componets/SmsVerificationDialog';
import LanguageSwitch from './componets/LanguageSwitch';
import { useLanguage } from './i18n/LanguageContext';

/**
 * Strona podpisu umowy (/f/sign/{token}): podgląd zamrożonego PDF, oświadczenie o akceptacji,
 * podpis kodem SMS (ten sam dialog co w formularzu) albo odmowa z powodem.
 */
const SignPage = () => {
    const { token = '' } = useParams();
    const { lang, t } = useLanguage();
    const [info, setInfo] = useState<SignInfo | null>(null);
    const [errorKey, setErrorKey] = useState<'unavailable' | 'already' | 'server' | null>(null);
    const [loading, setLoading] = useState(true);
    const [accepted, setAccepted] = useState(false);
    // Dialog SMS obsługuje obie akcje — podpis i odmowę (obie uwierzytelniane kodem).
    const [smsAction, setSmsAction] = useState<'sign' | 'reject' | null>(null);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [actionError, setActionError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await signingService.getInfo(token);
                if (!cancelled) setInfo(data);
            } catch (err) {
                const e = toApiError(err);
                if (!cancelled) {
                    setErrorKey(e.error === 'form_outdated' ? 'unavailable'
                        : e.error === 'form_already_submitted' ? 'already'
                        : 'server');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    // Po poprawnym kodzie SMS: podpis albo odmowa — zależnie od tego, co uruchomiło dialog.
    const handleProof = async (proof: string) => {
        setActionError(null);
        const updated = smsAction === 'reject'
            ? await signingService.reject(token, rejectReason.trim(), proof, lang)
            : await signingService.sign(token, proof, lang);
        setInfo(updated);
        setSmsAction(null);
        setRejectOpen(false);
    };

    const handleReject = () => {
        setActionError(null);
        setSmsAction('reject');
    };

    const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const formatTime = (iso?: string | null) => iso ? new Date(iso).toLocaleString(lang === 'en' ? 'en-GB' : 'pl-PL') : '';

    const renderBody = () => {
        if (loading) {
            return (
                <Alert severity="info" sx={{ mt: 4 }}>
                    <AlertTitle>{t('sign.loading')}</AlertTitle>
                </Alert>
            );
        }
        if (errorKey === 'unavailable') {
            return <Alert severity="warning" sx={{ mt: 4 }}><AlertTitle>{t('sign.unavailableTitle')}</AlertTitle>{t('sign.unavailableBody')}</Alert>;
        }
        if (errorKey === 'already') {
            return <Alert severity="info" sx={{ mt: 4 }}><AlertTitle>{t('sign.alreadyTitle')}</AlertTitle>{t('sign.alreadyBody')}</Alert>;
        }
        if (errorKey || !info) {
            return <Alert severity="error" sx={{ mt: 4 }}><AlertTitle>{t('error.server.title')}</AlertTitle>{t('sign.error')}</Alert>;
        }
        if (info.status === 'SIGNED') {
            return (
                <Alert severity="success" sx={{ mt: 4 }}>
                    <AlertTitle>{t('sign.doneTitle')}</AlertTitle>
                    {t('sign.doneBody')}
                    <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                        {t('sign.doneMeta', { time: formatTime(info.signedAt), phone: info.phoneMasked ?? '' })}
                    </Typography>
                </Alert>
            );
        }
        if (info.status === 'REJECTED') {
            return <Alert severity="warning" sx={{ mt: 4 }}><AlertTitle>{t('sign.rejectedTitle')}</AlertTitle>{t('sign.rejectedBody')}</Alert>;
        }
        if (info.status === 'CANCELLED') {
            return <Alert severity="warning" sx={{ mt: 4 }}><AlertTitle>{t('sign.unavailableTitle')}</AlertTitle>{t('sign.unavailableBody')}</Alert>;
        }

        const pdfUrl = signingService.documentUrl(token);
        // Parametry po kratce sa czytane przez wbudowana przegladarke PDF-ow, nie przez
        // nasz kod: chowaja jej pasek narzedzi i panel miniatur, zostawiajac sam,
        // przewijalny dokument. Dotyczy wylacznie podgladu osadzonego - przycisk
        // "otworz" ponizej prowadzi pod czysty adres, gdzie pasek jest potrzebny
        // do pobrania i wydruku.
        const pdfPodgladUrl = `${pdfUrl}#toolbar=0&navpanes=0`;
        const introHtml = t('sign.intro', { principal: escapeHtml(info.principalName), title: escapeHtml(info.title) });
        return (
            <>
                <Box sx={{
                    mt: 4, mb: 3, borderRadius: '8px', padding: '24px', backgroundColor: '#E0F2F1',
                    border: '1px solid #00796B', fontFamily: '"Poppins", sans-serif',
                }}>
                    <Typography variant="h5" gutterBottom>{t('sign.title')}</Typography>
                    <Typography variant="body1" gutterBottom dangerouslySetInnerHTML={{ __html: introHtml }} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>{t('sign.signer')}:</strong> {info.participant.firstName} {info.participant.lastName} ({info.participant.email})
                        {info.participant.companyName ? ` · ${info.participant.companyName}` : ''}
                        <br />
                        <strong>{t('sign.validTill')}:</strong> {formatTime(info.validTill)}
                        <br />
                        <Typography variant="caption" color="text.secondary">iForms ID: {info.documentId}</Typography>
                    </Typography>
                </Box>

                <Box sx={{ border: '1px solid #B2DFDB', borderRadius: '8px', overflow: 'hidden', height: { xs: '60vh', md: '75vh' }, backgroundColor: '#fff' }}>
                    <iframe title={info.title} src={pdfPodgladUrl} style={{ width: '100%', height: '100%', border: 0 }} />
                </Box>
                <Box sx={{ textAlign: 'right', mt: 1 }}>
                    <Button size="small" href={pdfUrl} target="_blank" rel="noopener noreferrer">{t('sign.openPdf')}</Button>
                </Box>

                <Box sx={{ mt: 3 }}>
                    <FormControlLabel
                        control={<Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)} sx={{ color: '#00796B', '&.Mui-checked': { color: '#00796B' } }} />}
                        label={<Typography variant="body1">{t('sign.accept')}</Typography>}
                        sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.5 } }}
                    />
                </Box>

                {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 3, mb: 10 }}>
                    <Button variant="outlined" color="error" onClick={() => setRejectOpen(true)}>
                        {t('sign.rejectButton')}
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        disabled={!accepted}
                        onClick={() => { setActionError(null); setSmsAction('sign'); }}
                        sx={{ backgroundColor: '#00796B', '&:hover': { backgroundColor: '#005A4F' }, px: 5 }}
                    >
                        {t('sign.signButton')}
                    </Button>
                </Box>

                <SmsVerificationDialog
                    open={smsAction !== null}
                    token={token}
                    onSigned={handleProof}
                    onClose={() => setSmsAction(null)}
                    sendCode={signingService.sendSmsCode}
                    verifyCode={signingService.verifySmsCode}
                />

                <Dialog open={rejectOpen && smsAction === null} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>{t('sign.rejectTitle')}</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 2 }}>{t('sign.rejectIntro')}</Typography>
                        <TextField
                            fullWidth multiline minRows={3}
                            label={t('sign.rejectReason')}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value.slice(0, 1000))}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setRejectOpen(false)} color="inherit">{t('sign.rejectCancel')}</Button>
                        <Button onClick={handleReject} color="error" variant="contained">{t('sign.rejectConfirm')}</Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    };

    return (
        <>
            <Box sx={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100, height: '84px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: '1px solid #00D2B4', padding: '0 16px', backgroundColor: '#1A202C',
            }}>
                <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', marginLeft: { xs: '1vw', md: '20px' } }}>
                    <img src={`${process.env.PUBLIC_URL}/MysticLogo.png`} alt="Mystic Creatures Festival" style={{ height: '64px', width: 'auto', display: 'block' }} />
                </Typography>
                {loading && (
                    <LinearProgress sx={{ height: 3, width: '100%', position: 'absolute', bottom: 0, left: 0, backgroundColor: '#00D2B4', '& .MuiLinearProgress-bar': { backgroundColor: '#B2DFDB' } }} />
                )}
            </Box>
            <Container maxWidth="md">
                <Box sx={{ marginTop: '108px' }}>
                    <LanguageSwitch />
                </Box>
                {renderBody()}
            </Container>
        </>
    );
};

export default SignPage;
